import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useStore, getCashbook, store, useClosedDays } from "@/lib/pos-store";
import { PageHeader, fmt } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { hasRole } from "@/lib/auth-store";

export const Route = createFileRoute("/cashbook/close")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "manager")) {
      throw redirect({ to: "/cashbook" });
    }
  },
  head: () => ({
    meta: [
      { title: "Close Day" },
      {
        name: "description",
        content: "Close the current day and start a new one.",
      },
    ],
  }),
  component: CloseDayPage,
});

function CloseDayPage() {
  const nav = useNavigate();
  const state = useStore((s) => s);
  const cb = getCashbook(state);
  const closedDays = useClosedDays();

  const now = new Date();
  // Default to today's date, but allow editing for midnight-shift handover
  const defaultDate = now.toISOString().slice(0, 10);
  const [closeDate, setCloseDate] = useState(defaultDate);
  const [confirming, setConfirming] = useState(false);

  const hasSales = state.sales.length > 0;
  const hasCash = state.cash.length > 0;
  const isEmpty = !hasSales && !hasCash;

  const handleClose = async () => {
    if (!closeDate) return;
    await store.closeDay(closeDate);
    toast.success(`Day closed for ${closeDate}`);
    nav({ to: "/cashbook" });
  };

  return (
    <div>
      <PageHeader
        title="Close Day"
        subtitle="Review totals before closing"
        right={
          <Button
            variant="outline"
            size="sm"
            onClick={() => nav({ to: "/cashbook" })}
          >
            Back
          </Button>
        }
      />

      <div className="space-y-4 px-4 pt-4">
        {/* Date picker — supports closing past-midnight / previous day */}
        <div className="rounded-xl border border-border bg-card p-4">
          <label className="text-xs font-medium text-muted-foreground">
            Closing for date
          </label>
          <div className="mt-1 flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <input
              type="date"
              value={closeDate}
              onChange={(e) => setCloseDate(e.target.value)}
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            For night shifts ending past midnight, set the date to the previous
            day.
          </p>
        </div>

        {/* Summary */}
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="mb-3 text-sm font-semibold">Day Summary</h3>

          <div className="space-y-2 text-sm">
            <SummaryRow
              label="Previous Balance"
              value={fmt(cb.previousBalance)}
            />
            <SummaryRow label="Total Sales" value={fmt(cb.salesTotal)} bold />
            <SummaryRow label="Cash Sales" value={fmt(cb.cashSales)} indent />
            <SummaryRow
              label="M-Pesa Sales"
              value={fmt(cb.mpesaSales)}
              indent
            />
            <SummaryRow label="Bank Sales" value={fmt(cb.bankSales)} indent />
            <SummaryRow label="Debts Paid" value={fmt(cb.debtsPaid)} />
            <Divider />
            <SummaryRow label="Purchases" value={fmt(cb.purchases)} />
            <SummaryRow label="Expenditure" value={fmt(cb.expenditure)} />
            <SummaryRow label="Debts Given" value={fmt(cb.debts)} />
            <SummaryRow label="Banking" value={fmt(cb.banking)} />
            <SummaryRow label="M-Pesa" value={fmt(cb.mpesa)} />
            <SummaryRow
              label="Cash Handed Over"
              value={fmt(cb.cashHandedOver)}
            />
            <Divider />
            <SummaryRow label="B.C.F" value={fmt(cb.bcf)} bold accent />
          </div>
        </div>

        {/* Warning for no-data */}
        {isEmpty && (
          <div className="flex items-start gap-3 rounded-xl border border-warning bg-warning/10 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning-foreground" />
            <div>
              <p className="text-sm font-medium text-warning-foreground">
                No data to close
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                There are no sales or cash entries for this day. Closing anyway
                will reset the day and carry forward the previous balance.
              </p>
            </div>
          </div>
        )}

        {/* Previously closed days info */}
        {closedDays.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="mb-2 text-sm font-semibold">
              Previously Closed Days
            </h3>
            <p className="text-xs text-muted-foreground">
              {closedDays.length} day{closedDays.length > 1 ? "s" : ""} have
              been closed. You can view them in the reports section.
            </p>
          </div>
        )}

        {/* Confirmation & Close button */}
        {!confirming ? (
          <Button
            className="w-full"
            size="lg"
            variant="destructive"
            onClick={() => setConfirming(true)}
          >
            <AlertTriangle className="mr-2 h-4 w-4" />
            Close Day
          </Button>
        ) : (
          <div className="space-y-2 rounded-xl border border-destructive bg-destructive/10 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div>
                <p className="text-sm font-semibold text-destructive">
                  Confirm closing {closeDate}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  This will archive all sales, cash entries and inventory for
                  this day. A new day will begin with B.C.F ({fmt(cb.bcf)}) as
                  the previous balance. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setConfirming(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleClose}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Confirm Close
              </Button>
            </div>
          </div>
        )}

        <div className="h-8" />
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  bold,
  accent,
  indent,
}: {
  label: string;
  value: string;
  bold?: boolean;
  accent?: boolean;
  indent?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${indent ? "pl-4" : ""}`}
    >
      <span
        className={`${bold ? "font-semibold" : ""} ${accent ? "text-primary" : "text-muted-foreground"} text-xs`}
      >
        {label}
      </span>
      <span
        className={`font-display ${bold ? "text-base font-bold" : "text-sm font-medium"} ${accent ? "text-primary" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

function Divider() {
  return <div className="my-1 border-t border-dashed border-border" />;
}
